import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Box, Tab, Tabs } from "@mui/material";
import { FetchFromBackoffice } from "../utils/requests";
import { WorkshopSession } from "../types/workshopSession";
import { InspectionPhotos } from "../components/molecules/inspectionPhotos";
import { VehicleOverview } from "../components/molecules/vehicleOverview";
import { DamageReports } from "../components/molecules/damageReports";
import { EditedVehicleInformation } from "../components/molecules/editVehicleInformationModal";
import { BubbleLoadingWidget } from "../components/molecules/bubbleLoadingWidget";
import SidebarLayout from "../components/layout/sidebarLayout";
import { buildManagePath, manageSidebarContent } from "../constants/sidebarContent";
import Page from "../components/atoms/page";
import { useSelectedWorkshop } from "../context/workshopContext";
import { VehicleProgressSlider } from "../components/molecules/vehicleProgressSlider";
const vehiclesSelection: [number, number] = [0, 0];

export const VehicleDetails = () => {
  const navigate = useNavigate();
  const { vin, workshopSessionId: workSessionId } = useParams();
  const { selectedOrganisationId: workshopId } = useSelectedWorkshop();
  const [currentWorkshopSession, setCurrentWorkshopSession] = useState<WorkshopSession | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const [tabIndex, setTabIndex] = useState<number>(0);

  const fetchCurrentWorkshopSession = useCallback(async () => {
    if (!workSessionId || !workshopId) {
      if (!workSessionId) {
        setIsLoading(false);
      }
      return;
    }

    setIsLoading(true);
    try {
      const data = await FetchFromBackoffice<WorkshopSession>(
        `portal/workshops/${workshopId}/session/${workSessionId}`
      );
      setCurrentWorkshopSession(data);
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  }, [workSessionId, workshopId]);

  useEffect(() => {
    fetchCurrentWorkshopSession();
  }, [fetchCurrentWorkshopSession]);

  const handleVehicleInformationSaved = (saved: EditedVehicleInformation) => {
    setCurrentWorkshopSession((prev) =>
      prev
        ? {
          ...prev,
          vehicle: {
            ...prev.vehicle,
            vin: saved.vin,
            make: saved.make,
            model: saved.model,
            year: saved.year,
          },
        }
        : prev
    );
  };

  const inspectionCount = (currentWorkshopSession?.inspections ?? []).length;

  const damageCount = (currentWorkshopSession?.damageReports ?? []).filter(
    (d) => !workSessionId || d.workSessionId === workSessionId
  ).length;

  const tabs = [
    {
      name: "Overview",
      pageSections: [
        <VehicleOverview
          vin={vin}
          currentWorkshopSession={currentWorkshopSession}
          onViewAllDamages={() => setTabIndex(2)}
          onSaved={handleVehicleInformationSaved}
        />
      ],
    },
    {
      name: `Inspection (${inspectionCount})`,
      pageSections: [
        <InspectionPhotos currentWorkshopSession={currentWorkshopSession} />,
      ],
    },
    {
      name: `Damages (${damageCount})`,
      pageSections: [
        <DamageReports vin={vin} workSessionId={workSessionId} currentWorkshopSession={currentWorkshopSession} />
      ],
    },
  ];

  return (
    <SidebarLayout
      selected={vehiclesSelection}
      onSelect={(selection) => navigate(buildManagePath(selection))}
      sidebarContent={manageSidebarContent}
    >
      <Page title="Vehicle Details">
        {isLoading ? (
          <BubbleLoadingWidget text="Loading vehicle..." />
        ) : (
          <>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                flexWrap: "wrap",
                gap: 2,
                marginBottom: 2,
              }}
            >
              <Tabs
                value={tabIndex}
                onChange={(_event, newValue) => setTabIndex(newValue)}
                aria-label="vehicle detail tabs"
                sx={{ minWidth: 0 }}
              >
                {tabs.map((t, index) => (
                  <Tab key={"tab_" + index} label={t.name} />
                ))}
              </Tabs>
              <VehicleProgressSlider
                workSessionId={currentWorkshopSession?.workSessionId}
                progress={currentWorkshopSession?.progress}
                updatedAt={currentWorkshopSession?.progressUpdatedAtUtc}
              />
            </Box>

            <Box sx={{ position: "relative", width: "100%" }}>
              {tabs.map((tab, index) => (
                <Box
                  key={`tab-panel-${index}`}
                  sx={{
                    display: tabIndex === index ? "flex" : "none",
                    flexDirection: "column",
                    gap: 4,
                    width: "100%"
                  }}
                >
                  {tab.pageSections.map((section, sIndex) => (
                    <Box key={`section-${index}-${sIndex}`} sx={{ width: "100%" }}>
                      {section}
                    </Box>
                  ))}
                </Box>
              ))}
            </Box>
          </>
        )}
      </Page>
    </SidebarLayout>
  );
};